import React, { useEffect, useState, useCallback } from "react";
import { Skeleton, Typography } from "antd";
import { useParams, useLocation } from "react-router-dom";
import { IAuditLogResponse, IAuditLogResult } from "../../interfaces/audit-log";
import { DEFAULT_PAGINATION } from "../../constants/pagination";
import { AuditLogServices } from "../../services/audit-log";
import { ISortInfo } from "../../interfaces/common";
import { formatDateDDMMYYYY, formatDateDDMMYYYY_HHMMSS, formatNumberFixed } from "../../utils/helper";
import BackButton from "../../components/back-button";
import { AuditItem } from "../../components/audit-trail/audit-trail";
import InfiniteAuditTrail from "../../components/audit-trail/infinite-audit-trail";

const { Text } = Typography;

const fieldLabels: Record<string, string> = {
  status: "Status",
  flag: "Flag",
  team: "Team",
  comment: "Comment",
  expected_supplier_payment_id: "ESP",
  is_reviewed: "Is Reviewed",
  esp_matching: "ESP Matching",
  sir_matching: "SIR Matching",
  is_lock_matching: "Lock Matching",
  sage_account_credit: "Credit",
  sage_account_debit: "Debit",
  under_overs_auto_match_code: "Unders & Overs",
  batch_name: "Batch Name",
  esp_update: "ESP Update",
  type: "Type",
  deleted_at: "Deleted At",
  matching_category: "Matching Category",
};

interface CreditNote {
  id: string | number;
  amount: number;
  note: string;
}

const WorkflowAudiTrailPage: React.FC<{ type?: "ap_workflow" | "esp_workflow" }> = ({ type = "ap_workflow" }) => {
  const [logs, setLogs] = useState<IAuditLogResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [sortInfo] = useState<ISortInfo>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0,
  });

  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const workflowType = location.pathname.split("/").filter(Boolean)[0];

  const fetchData = async () => {
    if (!id) {
      setLogs([]);
      setInitialLoading(false);
      return;
    }
    if (!hasMore && pagination.current !== 1) return;

    try {
      if (pagination.current === 1) setInitialLoading(true);
      else setLoading(true);

      const params = {
        tableName: type,
        recordId: id,
        isGetRawData: true,
        page: pagination.current,
        pageSize: pagination.pageSize,
        orderBy: sortInfo.orderBy,
        orderDirection: sortInfo.orderDirection,
      };

      const response: IAuditLogResponse = await AuditLogServices.getAuditLogs({ params });
      const newLogs = response.data.results || [];

      setLogs((prev) => (pagination.current === 1 ? newLogs : [...prev, ...newLogs]));
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta?.totalElements || 0,
      }));
      setHasMore(newLogs.length === pagination.pageSize);
    } catch (error) {
      console.error("Fetch audit logs error:", error);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps, no-sparse-arrays
  }, [id, pagination?.current, pagination?.pageSize, sortInfo, hasMore]);

  const handleLoadMore = () => {
    setPagination((prev) => ({ ...prev, current: prev.current + 1 }));
  };

  const formatValue = (field: string, value: any) => {
    if (value === null || value === undefined || value === "") return "blank";
    switch (field) {
      case "is_lock_matching":
        return value ? "lock" : "unlock";
      case "amount":
        return formatNumberFixed(value) ?? "blank";
      case "date":
        return formatDateDDMMYYYY(value) ?? "blank";
      default:
        if (typeof value === "boolean") return value ? "Yes" : "No";
        return String(value);
    }
  };

  const auditData: AuditItem[] = logs.map((log) => ({
    id: log.id,
    action: log.action,
    changes: log.changes.map((c) => ({
      field: fieldLabels[c.field] || c.field,
      from: formatValue(c.field, c.oldValue),
      to: formatValue(c.field, c.newValue),
    })),
    oldData: log.oldData ?? {},
    newData: log.newData ?? {},
    updatedAt: formatDateDDMMYYYY_HHMMSS(log.updatedAt)!,
    user: log.updatedByUser || "System",
  }));

  const transformItemDisplay = (
    item: AuditItem,
    change: {
      field: string;
    }
  ): React.ReactNode => {
    switch (change.field) {
      case "credit_note_data":
        return buildCreditNoteChanges(item);
      case "matching_data":
        return buildMatchingConditions(item);
      default:
        return null;
    }
  };

  const buildMatchingConditions = (item: AuditItem) => {
    const conditions: string[] = [];
    const match = item.newData?.matching_data ?? {};

    if (!match.matching_fileds) {
      // for esp workflow
      const espBooking = match.esp_data?.booking_ref ?? "N/A";
      const espAmount = match.esp_data?.amount ?? "N/A";
      conditions.push(`Supplier Code match with linked Supplier: ${match.supplier_data.supplier_code}`);
      conditions.push(`Booking Reference match ESP: ${espBooking}`);
      conditions.push(`Total Cost ${formatNumberFixed(espAmount)}`);
    } else {
      // Supplier Code
      if (match.matching_fileds?.includes("supplier_code")) {
        if (match.supplier_data?.supplier_code) {
          conditions.push(`Supplier Code match with linked Supplier: ${match.supplier_data.supplier_code}`);
        } else {
          conditions.push("Supplier Code match");
        }
      }

      // Booking Reference
      if (match.matching_fileds?.includes("booking_ref")) {
        const sirBooking = match.sir_data?.booking_ref ?? "N/A";
        const espBooking = match.esp_data?.booking_ref ?? "N/A";

        if (!espBooking) {
          conditions.push("Booking Reference (ESP) is missing");
        } else {
          conditions.push(`Booking Reference match (SIR: ${sirBooking}, ESP: ${espBooking})`);
        }
      }

      // TOL Total Cost
      if (match.matching_fileds?.includes("tol_amount")) {
        const isWithSubs = !!match.sir_data?.total_amount_with_subs;
        const title = isWithSubs ? "Total Cost (including Subsidiaries)" : "Total Cost";
        const sirAmount = isWithSubs ? match.sir_data?.total_amount_with_subs ?? "N/A" : match.sir_data?.amount ?? "N/A";
        const espAmount = match.esp_data?.amount ?? "N/A";

        if (match.supplier_data?.is_tolerance_percent && match.supplier_data?.tolerance_percent) {
          conditions.push(
            `${title} match within Tolerance ${match.supplier_data.tolerance_percent}% (SIR: ${formatNumberFixed(
              sirAmount
            )}, ESP: ${formatNumberFixed(espAmount)})`
          );
        }

        if (match.supplier_data?.is_tolerance_absolute && match.supplier_data?.tolerance_absolute) {
          conditions.push(
            `${title} match within Tolerance ${match.supplier_data.tolerance_absolute} (SIR: ${formatNumberFixed(
              sirAmount
            )}, ESP: ${formatNumberFixed(espAmount)})`
          );
        }
      }

      // Cost
      if (match.matching_fileds?.includes("amount")) {
        const isWithSubs = !!match.sir_data?.total_amount_with_subs;
        const title = isWithSubs ? "Total Cost (including Subsidiaries)" : "Total Cost";
        const sirAmount = isWithSubs ? match.sir_data?.total_amount_with_subs ?? "N/A" : match.sir_data?.amount ?? "N/A";
        const espAmount = match.esp_data?.amount ?? "N/A";
        conditions.push(`${title} match (SIR: ${formatNumberFixed(sirAmount)}, ESP: ${formatNumberFixed(espAmount)})`);
      }

      // Cost
      if (!match.matching_fileds?.includes("amount") && !match.matching_fileds?.includes("tol_amount")) {
        const isWithSubs = !!match.sir_data?.total_amount_with_subs;
        const title = isWithSubs ? "Total Cost (including Subsidiaries)" : "Total Cost";
        const sirAmount = isWithSubs ? match.sir_data?.total_amount_with_subs ?? "N/A" : match.sir_data?.amount ?? "N/A";
        const espAmount = match.esp_data?.amount ?? "N/A";
        conditions.push(
          `${title} outside Tolerance ${match.supplier_data.tolerance_percent ?? "N/A"}% and ${match.supplier_data.tolerance_absolute ?? "N/A"
          } (SIR: ${formatNumberFixed(sirAmount)}, ESP: ${formatNumberFixed(espAmount)})`
        );
      }

      // Lead Pax
      if (match.matching_fileds?.includes("lead_pax")) {
        const sirLeadPax = match.sir_data?.lead_pax ?? "N/A";
        const espLeadPax = match.esp_data?.lead_pax ?? "N/A";
        conditions.push(`Lead Pax Name match (SIR: ${sirLeadPax}, ESP: ${espLeadPax})`);
      }
    }



    return (
      <div className="matching-data">
        <Text strong>Matching Conditions:</Text>
        <ul style={{ margin: 0, paddingLeft: 16, listStyle: "none" }}>
          {conditions.map((c) => (
            <li key={c}>
              <Text>• {c}</Text>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const buildCreditNoteChanges = (item: AuditItem) => {
    const oldNotes: CreditNote[] = item.oldData?.credit_note_data ?? [];
    const newNotes: CreditNote[] = item.newData?.credit_note_data ?? [];
    const changes: string[] = [];

    // 1. Check created or updated
    newNotes.forEach((newNote) => {
      const oldNote = oldNotes.find((o) => String(o.id) === String(newNote.id));
      if (!oldNote) {
        // Created
        if (newNote.amount != null) {
          changes.push(`Credit Amount from blank to "${formatNumberFixed(newNote.amount)}"`);
        }
        if (newNote.note) {
          changes.push(`Credit Note from blank to "${newNote.note}"`);
        }
      } else {
        // Updated
        if (oldNote.amount !== newNote.amount) {
          changes.push(
            `Credit Amount from ${formatNumberFixed(oldNote.amount) ?? "blank"} to ${formatNumberFixed(newNote.amount) ?? "blank"}`
          );
        }
        if (oldNote.note !== newNote.note) {
          changes.push(`Credit Note from ${oldNote.note ?? "blank"} to ${newNote.note ?? "blank"}`);
        }
      }
    });

    // 2. Check deleted
    // oldNotes.forEach(oldNote => {
    //   const exists = newNotes.some(n => String(n.id) === String(oldNote.id));
    //   if (!exists) {
    //     changes.push(
    //       `Removed Credit Note (id: ${oldNote.id}, amount: ${oldNote.amount}, note: ${oldNote.note})`
    //     );
    //   }
    // });

    if (changes.length === 0) return null;
    // const from = item?.oldData?.is_credit_note_active ? "Yes" : "No";
    // const to = item?.newData?.is_credit_note_active ? "Yes" : "No";
    return (
      <div className="credit-note-changes">
        <ul style={{ margin: 0, paddingLeft: 16 }}>
          {changes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      </div>
    );
  };

  const getLabelBackTo = () => {
    switch (workflowType) {
      case "flight-workflow":
        return "Flight";
      case "tour-event-workflow":
        return "Tour Event";
      case "ap-workflow":
        return "Ap";

      default:
        return "Other";
    }
  };

  return (
    <div className="audit-trail-page">
      <BackButton
        label={`${getLabelBackTo()} Workflow List`}
      />
      <h2 className="title-page" style={{ marginBottom: 16 }}>
        Audit Trail
      </h2>
      <div style={{ borderBottom: "1px solid #e8e8e8", marginBottom: 30 }}></div>

      {initialLoading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <InfiniteAuditTrail
          items={auditData}
          loading={loading}
          hasMore={hasMore}
          initialLoading={initialLoading}
          onLoadMore={handleLoadMore}
          transformItemDisplay={transformItemDisplay}
        />
      )}
    </div>
  );
};

export default WorkflowAudiTrailPage;
